import { RecommendationDto, RecommendationAttemptDto, RecommendationAttemptStatusDto } from '../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { AssessmentGoalDto } from '../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  PostPlayerPageContext,
  EbookReadingPageContext,
  OsmosisVideoPageContext,
  TopicOverviewPageContext
} from './sherpath-ai-context.utilities';
import {
  getAssessmentGoalFromRecommendation,
  getAssessmentSubmissionByAttempt,
  getContentTypePassNumber,
  getNextRecommendation,
  getRecommendationContentId,
  getRecommendationFromAttempt,
  getRecommendationTitle
} from './remediation.utilities';
import { getEbookRecommendationData, getEbookRecommendationSubsection } from './ebook.utilities';
import { RemediationBaseState } from '../hocs/with-remediation-base/withRemediationBase.hoc';

/**
 * Formats seconds into a human-readable time string (e.g., "1h 5m 30s" or "5m 30s")
 * @param seconds Total seconds to format
 * @returns Formatted time string or undefined if seconds is null/undefined
 */
export const formatTimeSpent = (seconds: number | undefined): string | undefined => {
  if (seconds == null || seconds === 0) {
    return undefined;
  }

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  return `${minutes}m ${secs}s`;
};

/**
 * Builds the post-player page context from recommendation attempt data
 */
export const buildPostPlayerPageContext = (params: {
  baseState: RemediationBaseState;
  attempt: RecommendationAttemptDto;
}): PostPlayerPageContext => {
  const { baseState, attempt } = params;

  const recommendation = getRecommendationFromAttempt(baseState.recommendations, attempt);

  if (!recommendation) {
    return null;
  }

  const submission = getAssessmentSubmissionByAttempt(attempt, baseState.attemptAssessmentSubmissions);
  const scoreToPass = getContentTypePassNumber(recommendation, baseState);
  const assessmentGoal = baseState.eolsAssessment
    ? getAssessmentGoalFromRecommendation(baseState.eolsAssessment, recommendation)
    : null;
  const nextRecommendation = assessmentGoal ? getNextRecommendation(baseState, assessmentGoal) : null;

  // Exclude the just-completed recommendation from the next recommendation
  const nextRecommendationTitle = (nextRecommendation && nextRecommendation.id !== recommendation.id)
    ? getRecommendationTitle(nextRecommendation)
    : undefined;

  // Determine status based on whether there's a submission and the score
  let status: RecommendationAttemptStatusDto;
  if (!submission) {
    // No submission means the activity was not completed (closed before finishing)
    status = RecommendationAttemptStatusDto.IN_PROGRESS;
  } else if (submission.score >= scoreToPass) {
    // Has submission and passed the threshold
    status = RecommendationAttemptStatusDto.COMPLETED_PASSED;
  } else {
    // Has submission but did not pass the threshold
    status = RecommendationAttemptStatusDto.COMPLETED_FAILED;
  }

  return {
    completedTitle: getRecommendationTitle(recommendation),
    completedType: recommendation.contentType,
    status,
    timeSpent: formatTimeSpent(submission?.timeSpent),
    nextRecommendationTitle
  };
};

/**
 * Builds the ebook reading page context from recommendation data
 */
export const buildEbookReadingPageContext = (params: {
  recommendation: RecommendationDto;
}): EbookReadingPageContext => {
  const { recommendation } = params;

  const ebookData = getEbookRecommendationData(recommendation);
  const subSection = getEbookRecommendationSubsection(recommendation);

  if (!ebookData || !subSection) {
    return null;
  }

  // Build chapter hierarchy from breadcrumbs
  const chapterHierarchy = ebookData.breadCrumbs
    ? ebookData.breadCrumbs.map(crumb => crumb.title).join(' > ')
    : undefined;

  return {
    bookTitle: ebookData.title,
    isbn: ebookData.isbn,
    sectionTitle: subSection.title || undefined,
    chapterHierarchy,
    pageRange: subSection.page_range[0] === subSection.page_range[1]
      ? `${subSection.page_range[0]}`
      : `${subSection.page_range[0]}-${subSection.page_range[1]}`
  };
};

/**
 * Builds the Osmosis video page context from recommendation data and transcript
 */
export const buildOsmosisVideoPageContext = (params: {
  recommendation: RecommendationDto;
  assessmentGoal: AssessmentGoalDto;
  transcript: string;
}): OsmosisVideoPageContext => {
  const { recommendation, assessmentGoal, transcript } = params;

  const contentId = getRecommendationContentId(recommendation);

  if (!contentId) {
    return null;
  }

  return {
    videoTitle: getRecommendationTitle(recommendation),
    contentId,
    topicName: assessmentGoal?.text || undefined,
    transcript: transcript || undefined
  };
};

/**
 * Builds the Topic Overview page context from recommendation data
 */
export const buildTopicOverviewPageContext = (params: {
  recommendation: RecommendationDto;
}): TopicOverviewPageContext => {
  const { recommendation } = params;

  const contentId = getRecommendationContentId(recommendation);

  if (!contentId) {
    return null;
  }

  return {
    topicOverviewTitle: getRecommendationTitle(recommendation),
    contentId
  };
};
